import React, { useState, useEffect } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config/api';


const DepartmentEmployeesImprovementPlans = () => {
  const [improvementPlans, setImprovementPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [supervisorComment, setSupervisorComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchImprovementPlans();
  }, []);

  useEffect(() => {
    filterPlans();
  }, [improvementPlans, searchQuery, selectedYear]);

  const fetchImprovementPlans = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/improvement-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.length > 0) {
        setImprovementPlans(response.data);
        
        const years = [...new Set(response.data
          .map(plan => plan.year)
          .filter(year => year !== null && year !== undefined)
        )].sort((a, b) => b - a);
        setAvailableYears(years);
        
        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } else {
        setImprovementPlans([]);
      }
    } catch (error) {
      console.error('Error fetching improvement plans:', error);
      setError('Failed to fetch improvement plans');
    } finally {
      setLoading(false);
    }
  };

  const filterPlans = () => {
    let filtered = [...improvementPlans];

    if (selectedYear) {
      filtered = filtered.filter(plan => 
        plan.year !== null && 
        plan.year !== undefined && 
        plan.year.toString() === selectedYear
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plan => 
        (plan.employee_name && plan.employee_name.toLowerCase().includes(query)) ||
        (plan.employee_email && plan.employee_email.toLowerCase().includes(query)) ||
        (plan.strengths && plan.strengths.toLowerCase().includes(query)) ||
        (plan.weaknesses && plan.weaknesses.toLowerCase().includes(query)) ||
        (plan.employee_comment && plan.employee_comment.toLowerCase().includes(query)) ||
        (plan.training_needs && plan.training_needs.toLowerCase().includes(query)) ||
        (plan.supervisor_comment && plan.supervisor_comment.toLowerCase().includes(query))
      );
    }

    setFilteredPlans(filtered);
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPlan(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const splitToList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  const updatePlanStatus = async (planId, status, extra = {}) => {
    try {
      setActionLoading(true);
      const token = Cookies.get('token');
      await axios.patch(`${API_BASE_URL}/api/improvement-plans/${planId}/`, {
        approval_status: status,
        ...extra
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success(`Plan ${status}`);
      await fetchImprovementPlans();
      setSelectedPlan(prev => prev ? { ...prev, approval_status: status, ...extra } : prev);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () => {
    if (!selectedPlan?.id) return;
    setSupervisorComment('');
    setApproveOpen(true);
  };

  const handleReject = () => {
    if (!selectedPlan?.id) return;
    setRejectionReason('');
    setRejectOpen(true);
  };

  const submitApprove = async () => {
    if (!selectedPlan?.id) return;
    await updatePlanStatus(selectedPlan.id, 'approved', { supervisor_comment: supervisorComment });
    setApproveOpen(false);
  };

  const submitReject = async () => {
    if (!selectedPlan?.id) return;
    await updatePlanStatus(selectedPlan.id, 'rejected', { rejection_reason: rejectionReason });
    setRejectOpen(false);
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1">
            <i className="ti ti-circle-check-filled" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-1">
            <i className="ti ti-circle-x-filled" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
            <i className="ti ti-clock" />
            Pending
          </span>
        );
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Employee Improvement Plans</h4>
              <p className="text-muted mb-0 fs-13">View, filter, and manage department employee performance improvement plans</p>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchImprovementPlans}
                disabled={loading}
                title="Refresh"
              >
                <i className={`ti ti-refresh me-1 ${loading ? 'spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters Bar Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-8">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control ps-5"
                      placeholder="Search by employee name, email, strengths, weaknesses, comments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label mb-0 text-nowrap text-muted fs-14">Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="form-select"
                    >
                      <option value="">All Years</option>
                      {availableYears.map(year => (
                        <option key={year} value={year.toString()}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary line */}
          <div className="d-flex align-items-center justify-content-between mb-3 px-1">
            <span className="text-muted fs-13">
              Showing <strong className="text-dark">{filteredPlans.length}</strong> improvement plan{filteredPlans.length !== 1 ? 's' : ''}
              {selectedYear && ` for ${selectedYear}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </span>
          </div>

          {/* Table / Main Section */}
          {loading ? (
            <div className="card shadow-sm p-5 text-center">
              <div className="spinner-border text-primary mx-auto mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0 fs-13">Loading improvement plans...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger shadow-sm d-flex align-items-center justify-content-between" role="alert">
              <div>
                <i className="ti ti-alert-circle me-2" />
                {error}
              </div>
              <button onClick={fetchImprovementPlans} className="btn btn-sm btn-outline-danger">
                Try Again
              </button>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="card shadow-sm p-5 text-center text-muted">
              <i className="ti ti-message-off fs-36 mb-2 text-secondary opacity-50" />
              <h5 className="fw-semibold text-dark">No Improvement Plans Found</h5>
              <p className="mb-0 fs-13">
                {searchQuery || selectedYear 
                  ? 'Try adjusting your search criteria or filters.'
                  : 'No improvement plans have been submitted yet.'
                }
              </p>
            </div>
          ) : (
            <div className="card shadow-sm mb-4">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Employee</th>
                      <th>Year</th>
                      <th>Status</th>
                      <th className="text-end pe-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlans.map((plan) => (
                      <tr key={plan.id}>
                        <td className="ps-3">
                          <div className="d-flex align-items-center gap-2">
                            <div className="avatar avatar-sm bg-light text-primary rounded-circle d-flex align-items-center justify-content-center border">
                              <i className="ti ti-user fs-16" />
                            </div>
                            <div>
                              <div className="fw-semibold text-dark">{plan.employee_name || 'Unknown Employee'}</div>
                              <div className="text-muted fs-12">{plan.employee_email || 'No email provided'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            <i className="ti ti-calendar me-1 text-muted" />
                            {plan.year || 'N/A'}
                          </span>
                        </td>
                        <td>{renderStatusBadge(plan.approval_status)}</td>
                        <td className="text-end pe-3">
                          <button
                            type="button"
                            onClick={() => handleViewPlan(plan)}
                            className="btn btn-sm btn-light text-primary"
                            title="View full details"
                          >
                            <i className="ti ti-eye me-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>

      {/* Plan Details Modal */}
      {modalOpen && selectedPlan && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Improvement Plan Details</h5>
                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="p-3 bg-light rounded border">
                      <h6 className="fw-bold text-dark mb-2">Strengths</h6>
                      {splitToList(selectedPlan.strengths).length > 0 ? (
                        <ul className="mb-0 ps-3">
                          {splitToList(selectedPlan.strengths).map((item, idx) => (
                            <li key={`s-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0 fs-13">No strengths listed</p>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="p-3 bg-light rounded border">
                      <h6 className="fw-bold text-dark mb-2">Weaknesses</h6>
                      {splitToList(selectedPlan.weaknesses).length > 0 ? (
                        <ul className="mb-0 ps-3">
                          {splitToList(selectedPlan.weaknesses).map((item, idx) => (
                            <li key={`w-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0 fs-13">No weaknesses listed</p>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="p-3 bg-light rounded border">
                      <h6 className="fw-bold text-dark mb-2">Training Needs</h6>
                      {splitToList(selectedPlan.training_needs).length > 0 ? (
                        <ul className="mb-0 ps-3">
                          {splitToList(selectedPlan.training_needs).map((item, idx) => (
                            <li key={`t-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0 fs-13">No training needs specified</p>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="p-3 bg-light rounded border">
                      <h6 className="fw-bold text-dark mb-1">Employee Comment</h6>
                      <p className="text-dark mb-0 fs-14" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedPlan.employee_comment || 'No comment provided'}
                      </p>
                    </div>
                  </div>

                  {(selectedPlan.approval_status === 'approved' || selectedPlan.approval_status === 'rejected') && (
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border">
                        <h6 className="fw-bold text-dark mb-1">Supervisor Comment</h6>
                        <p className="text-dark mb-0 fs-14" style={{ whiteSpace: 'pre-wrap' }}>
                          {selectedPlan.supervisor_comment || 'No supervisor comment provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedPlan.approval_status === 'rejected' && selectedPlan.rejection_reason && (
                    <div className="col-12">
                      <div className="alert alert-danger mb-0">
                        <h6 className="fw-bold mb-1 d-flex align-items-center gap-1">
                          <i className="ti ti-alert-triangle" />
                          Rejection Reason
                        </h6>
                        <p className="mb-0 fs-14" style={{ whiteSpace: 'pre-wrap' }}>
                          {selectedPlan.rejection_reason}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedPlan.date_signed && (
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2 text-muted fs-13">
                        <i className="ti ti-calendar-event" />
                        <span>Date Signed: {formatDate(selectedPlan.date_signed)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                {selectedPlan?.approval_status === 'pending' && (
                  <div className="d-flex gap-2 me-auto">
                    <button type="button" onClick={handleReject} disabled={actionLoading} className="btn btn-outline-danger">
                      Reject
                    </button>
                    <button type="button" onClick={handleApprove} disabled={actionLoading} className="btn btn-success">
                      Approve
                    </button>
                  </div>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {approveOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Approve Improvement Plan</h5>
                <button type="button" className="btn-close" onClick={() => setApproveOpen(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-medium">Supervisor Comment (optional)</label>
                  <textarea
                    value={supervisorComment}
                    onChange={(e) => setSupervisorComment(e.target.value)}
                    placeholder="Enter supervisor comment..."
                    rows={3}
                    className="form-control"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setApproveOpen(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="button" onClick={submitApprove} disabled={actionLoading} className="btn btn-success">
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Approving...
                    </>
                  ) : (
                    'Confirm Approve'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {rejectOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Reject Improvement Plan</h5>
                <button type="button" className="btn-close" onClick={() => setRejectOpen(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-medium">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide the reason for rejection..."
                    rows={3}
                    className="form-control"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setRejectOpen(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReject}
                  disabled={actionLoading || !rejectionReason.trim()}
                  className="btn btn-danger"
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Rejecting...
                    </>
                  ) : (
                    'Confirm Reject'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentEmployeesImprovementPlans;
